/**
 * Componente de Distribuição por Provider PGMQ
 * 
 * Exibe gráfico de pizza com distribuição de mensagens por provider
 * (z-api, notificame, wuzapi, evolution, chatwoot).
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import { useFlowQueueByProviderPgmq } from '@/hooks/useFlowQueueMonitorPgmq';

// Cores para cada provider
const PROVIDER_COLORS: Record<string, string> = {
  'z-api': 'hsl(var(--chart-1))',
  'notificame': 'hsl(var(--chart-2))',
  'wuzapi': 'hsl(var(--chart-3))',
  'evolution': 'hsl(var(--chart-4))',
  'chatwoot': 'hsl(var(--chart-5))',
};

// Labels amigáveis para cada provider
const PROVIDER_LABELS: Record<string, string> = {
  'z-api': 'Z-API',
  'notificame': 'Notificame',
  'wuzapi': 'WuzAPI',
  'evolution': 'Evolution',
  'chatwoot': 'Chatwoot',
};

// Configuração do gráfico
const chartConfig = {
  'z-api': { label: 'Z-API', color: 'hsl(var(--chart-1))' },
  'notificame': { label: 'Notificame', color: 'hsl(var(--chart-2))' },
  'wuzapi': { label: 'WuzAPI', color: 'hsl(var(--chart-3))' },
  'evolution': { label: 'Evolution', color: 'hsl(var(--chart-4))' },
  'chatwoot': { label: 'Chatwoot', color: 'hsl(var(--chart-5))' },
};

export function PgmqProviderDistribution() {
  const { data: providerData, isLoading } = useFlowQueueByProviderPgmq();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Distribuição por Provider</CardTitle>
          <CardDescription>Providers utilizados</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Formatar dados para o gráfico
  const chartData = (providerData || [])
    .filter(item => item.count > 0)
    .map((item) => ({
      name: PROVIDER_LABELS[item.provider_slug] || item.provider_slug,
      value: item.count,
      provider: item.provider_slug,
    }));

  const totalMessages = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Distribuição por Provider</CardTitle>
        <CardDescription>
          Total: {totalMessages} mensagens nas últimas 24h
        </CardDescription>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Nenhum dado disponível
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={PROVIDER_COLORS[entry.provider] || `hsl(var(--chart-${(index % 5) + 1}))`}
                    />
                  ))}
                </Pie>
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
